import { Theme } from "@mui/material";
import type { SxProps } from "@mui/material/styles";
import { makeStyles } from "tss-react/mui";

// One item of MUI's `sx` array form (object, theme callback or `false`).
type SxItem = Exclude<SxProps<Theme>, ReadonlyArray<unknown>> | boolean;

// Normalises an optional `sx` prop to a spreadable array, so a component can
// merge its own styles with the caller's: `sx={[base, ...mergeSx(sx)]}`.
export const mergeSx = (sx?: SxProps<Theme>): readonly SxItem[] =>
  Array.isArray(sx) ? sx : sx ? [sx] : [];

// Disabled state of a button sitting on an inverted surface (filter bars,
// dark panels): MUI's default disabled colours are black-based and vanish
// there, so derive them from the bar's own text colour instead.
// Nancie surfaces: a focused field is outlined in the Nancie violet in both
// modes, instead of the theme's primary.
export const nancieFocusSx = {
  "& .MuiOutlinedInput-root.Mui-focused, & .MuiPickersOutlinedInput-root.Mui-focused":
    { borderColor: "nancie.main" },
  "& .MuiInputLabel-root.Mui-focused": { color: "nancie.main" },
} as const;

export const disabledOnInvertedSx = {
  "&.Mui-disabled": {
    backgroundColor:
      "color-mix(in srgb, var(--mui-palette-filter-text) 12%, transparent)",
    color: "filter.textDisabled",
  },
} as const;

export const genericTableStyles = makeStyles()((theme: Theme) => ({
  root: {
    border: 0,
    WebkitFontSmoothing: "auto",
    letterSpacing: "normal",
    "& .MuiDataGrid-main": {
      borderBottom: `1px solid ${theme.vars.palette.background.paper}`,
    },
    "& .MuiDataGrid-paper": {
      backgroundColor: theme.vars.palette.background.paper,
    },
    "& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight": {
      backgroundColor: theme.vars.palette.background.paper,
    },
    "& .MuiDataGrid-columnsContainer": {
      border: "none",
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      color: theme.vars.palette.text.secondary,
      fontSize: "0.625rem",
      overflow: "hidden",
      textOverflow: "ellipsis",
      display: "-webkit-box",
      lineClamp: 2,
      boxOrient: "vertical",
      whiteSpace: "initial",
      lineHeight: "normal",
    },
    "& .MuiDataGrid-columnHeader, .MuiDataGrid-cell": {
      "& .MuiDataGrid-columnSeparator": {
        color: theme.vars.palette.background.inverted,
      },
      "&:last-child": {
        "& .MuiDataGrid-columnSeparator": {
          display: "none",
        },
      },
    },
    "& .MuiDataGrid-columnHeader.separationColumnRight": {
      borderRight: `2px solid ${theme.vars.palette.background.default}`,

      "& .MuiDataGrid-columnSeparator": {
        right: "-13px",
        color: theme.vars.palette.background.inverted,
      },
    },
    "& .MuiDataGrid-columnHeader.separationColumnLeft": {
      borderLeft: `2px solid ${theme.vars.palette.background.default}`,

      "& .MuiDataGrid-columnSeparator": {
        right: "-13px",
        color: theme.vars.palette.background.inverted,
      },
    },
    "& .MuiDataGrid-row:hover": {
      cursor: "pointer",
    },
    "& .MuiDataGrid-cell": {
      borderBottom: `1px solid ${theme.vars.palette.background.default}`,

      "&.separationColumnRight": {
        borderRight: `2px solid ${theme.vars.palette.background.default}`,
      },
      "&.separationColumnLeft": {
        borderLeft: `2px solid ${theme.vars.palette.background.default}`,
      },
    },
    "& .MuiPaginationItem-root": {},
    "& .MuiTablePagination-root": {
      color: theme.vars.palette.text.secondary,
      "& .MuiTablePagination-selectIcon": {
        color: theme.vars.palette.text.secondary,
      },
      "& .MuiButtonBase-root.Mui-disabled": {
        color: theme.vars.palette.action.disabled,
      },
    },
    "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus-within":
      {
        outline: "none",
      },
    "& .MuiDataGrid-checkboxInput svg": {
      fontSize: "1rem",
    },
    "& .MuiDataGrid-columnHeader--sorted.MuiDataGrid-columnHeader--sortable.MuiDataGrid-columnHeader":
      {
        outline: `solid 1px ${theme.vars.palette.primary.main}`,
        outlineWidth: "1px",
        outlineOffset: "-1px",
      },
    "& .MuiDataGrid-menuIcon": {
      marginLeft: 0,
      width: "auto",
      visibility: "visible",

      "& button": {
        padding: 0,
        "&:hover": {
          color: theme.vars.palette.primary.main,
        },
      },
    },
    // Force right pinned column to padding 0 because of Mui bug.
    // Sometimes Mui add a padding right even if no scroll in table wich cause a gap.
    "& .MuiDataGrid-pinnedColumnHeaders--right": {
      paddingRight: "0 !important",
    },
  },
}));
